// ECharts paints on a canvas, so it cannot read the `--v-theme-*` CSS variables:
// every colour it draws has to be handed over as a value. These come from the
// active Vuetify theme (src/plugins/vuetify.ts); in light they are exactly
// ECharts' own defaults, so light charts look as they did before theming.

export interface IChartThemeColors {
  label: string;
  axis: string;
  grid: string;
  legend: string;
  tooltip: string;
  tooltipText: string;
  surface: string;
  surfaceSubtle: string;
  text: string;
  textMuted: string;
  paid: string;
}

interface IEchartsThemeAxis {
  axisLine: { lineStyle: { color: string } };
  axisTick: { lineStyle: { color: string } };
  axisLabel: { color: string };
  splitLine: { lineStyle: { color: string[] } };
}

export interface IEchartsTheme {
  darkMode: boolean;
  backgroundColor: string;
  legend: { textStyle: { color: string } };
  tooltip: { backgroundColor: string; borderColor?: string; textStyle: { color: string } };
  categoryAxis: IEchartsThemeAxis;
  valueAxis: IEchartsThemeAxis;
}

export const getChartThemeColors = (colors: Readonly<Record<string, string>>): IChartThemeColors => ({
  label: colors['sc-dash-chart-label'],
  axis: colors['sc-dash-chart-axis'],
  grid: colors['sc-dash-chart-grid'],
  // ECharts' legend #333 and tooltip #fff / #666. Charts colour their legend
  // text from this, not `on-surface`: Vuetify only derives `on-*` colours for
  // some theme keys, so `colors['on-surface']` is undefined, and ECharts reads an
  // explicit undefined as its dark #333 default -- invisible on a dark card.
  legend: colors['sc-text-strong'],
  tooltip: colors['sc-surface-raised'],
  tooltipText: colors['sc-text-subtle'],
  surface: colors.surface,
  surfaceSubtle: colors['sc-surface-subtle'],
  text: colors['sc-dash-text'],
  textMuted: colors['sc-dash-text-muted'],
  paid: colors['sc-dash-paid'],
});

export const getEchartsTheme = (colors: IChartThemeColors, isDark: boolean): IEchartsTheme => {
  const axis: IEchartsThemeAxis = {
    axisLine: { lineStyle: { color: colors.axis } },
    axisTick: { lineStyle: { color: colors.axis } },
    axisLabel: { color: colors.axis },
    splitLine: { lineStyle: { color: [colors.grid] } },
  };

  return {
    // Lets ECharts pick light text for labels drawn outside a shape on a dark card.
    darkMode: isDark,
    backgroundColor: 'transparent',
    legend: { textStyle: { color: colors.legend } },
    tooltip: {
      backgroundColor: colors.tooltip,
      // Unset, ECharts rings an axis tooltip in white (and an item tooltip in its
      // series colour): invisible on a light card, a glaring outline on a dark one.
      ...(isDark && { borderColor: colors.tooltip }),
      textStyle: { color: colors.tooltipText },
    },
    categoryAxis: axis,
    valueAxis: axis,
  };
};

const MIN_MARK_CONTRAST = 3;

const hexToRgb = (hex: string): number[] => {
  const value = hex.replace('#', '');

  return [0, 2, 4].map(offset => parseInt(value.slice(offset, offset + 2), 16));
};

const relativeLuminance = (rgb: number[]): number => {
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255;

    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a: number[], b: number[]): number => {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);

  return (light + 0.05) / (dark + 0.05);
};

// Some series colours come from the API (the comparables trend accents), and on
// a dark card a few of them -- the near-black ink, a deep plum -- sit under the
// 3:1 a chart mark needs against its background (WCAG 1.4.11). Those are mixed
// toward white in 10% steps until they reach it, so the hue stays recognisable.
// Light cards, and colours that already pass, keep the colour as sent.
export const getReadableMarkColor = (color: string, background: string, isDark: boolean): string => {
  if (!isDark || !/^#[0-9a-f]{6}$/i.test(color) || !/^#[0-9a-f]{6}$/i.test(background)) {
    return color;
  }

  const source = hexToRgb(color);
  const backgroundRgb = hexToRgb(background);

  if (contrast(source, backgroundRgb) >= MIN_MARK_CONTRAST) {
    return color;
  }

  for (let step = 1; step <= 10; step++) {
    const mixed = source.map(channel => Math.round(channel + (255 - channel) * step / 10));

    if (contrast(mixed, backgroundRgb) >= MIN_MARK_CONTRAST) {
      return `#${mixed.map(channel => channel.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
    }
  }

  return color;
};
